<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  symbol: string
  timeframe: string
  threshold: number
  showCandles: boolean
  loading?: boolean
  timeframes?: string[]
  symbols?: string[]
  hideCandlesToggle?: boolean
  hideRefresh?: boolean
}>()

const emit = defineEmits<{
  'update:symbol': [value: string]
  'update:timeframe': [value: string]
  'update:threshold': [value: number]
  'update:showCandles': [value: boolean]
  refresh: []
}>()

const DEFAULT_SYMBOLS = ['BTCUSDT', 'ETHUSDT']
const DEFAULT_TFS = ['1m', '3m', '5m', '15m', '30m', '1h', '2h', '4h', '1d']

const symbolList = computed(() =>
  props.symbols?.length ? props.symbols : DEFAULT_SYMBOLS,
)
const symbolOptions = computed(() =>
  symbolList.value.map((s) => ({ value: s, label: coinLabel(s) })),
)
const tfList = computed(() =>
  props.timeframes?.length ? props.timeframes : DEFAULT_TFS,
)

function coinLabel(sym: string) {
  return sym.endsWith('USDT') ? sym.slice(0, -4) : sym
}
</script>

<template>
  <div
    dir="ltr"
    class="panel-glass rounded-[1.75rem] p-3 sm:rounded-[2rem] sm:p-4 md:p-5"
  >
    <div
      class="panel-glass-content flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end"
    >
      <div class="min-w-[8rem] space-y-2 sm:min-w-[10rem]">
        <label class="block text-[10px] font-bold uppercase tracking-wider text-zinc-400">
          Pair
        </label>
        <SelectControl
          :selected="symbol"
          :options="symbolOptions"
          size="md"
          class="min-w-[10rem]"
          aria-label="Pair"
          @change="emit('update:symbol', $event)"
        />
      </div>

      <div class="space-y-2">
        <label class="block text-[10px] font-bold uppercase tracking-wider text-zinc-400">
          Timeframe
        </label>
        <div class="flex flex-wrap gap-2" role="tablist" aria-label="Timeframe">
          <button
            v-for="tf in tfList"
            :key="tf"
            type="button"
            class="panel-glass-btn rounded-full px-3.5 py-2 text-sm font-medium sm:px-4"
            :class="{ 'panel-glass-btn--active': tf === timeframe }"
            @click="emit('update:timeframe', tf)"
          >
            {{ tf }}
          </button>
        </div>
      </div>

      <div class="min-w-0 flex-1 space-y-2 lg:min-w-[14rem]">
        <label class="block text-[10px] font-bold uppercase tracking-wider text-zinc-400">
          Sensitivity
          <span class="ms-1 font-mono normal-case tracking-normal text-zinc-500 dark:text-zinc-400">
            {{ threshold.toFixed(2) }}
          </span>
        </label>
        <input
          class="zone-slider w-full"
          type="range"
          min="0.05"
          max="0.95"
          step="0.05"
          :value="threshold"
          @input="emit('update:threshold', Number(($event.target as HTMLInputElement).value))"
        >
      </div>

      <label
        v-if="!hideCandlesToggle"
        class="panel-glass-btn inline-flex cursor-pointer items-center gap-2 rounded-full px-3.5 py-2.5 text-sm"
      >
        <input
          type="checkbox"
          class="size-3.5 rounded border-zinc-300 text-primary-500 focus:ring-primary-400/30 dark:border-zinc-600"
          :checked="showCandles"
          @change="emit('update:showCandles', ($event.target as HTMLInputElement).checked)"
        >
        Candles
      </label>

      <button
        v-if="!hideRefresh"
        type="button"
        class="panel-glass-btn rounded-full px-4 py-2.5 text-sm font-semibold disabled:cursor-wait disabled:opacity-60"
        :class="{ 'panel-glass-btn--active': loading }"
        :disabled="loading"
        @click="emit('refresh')"
      >
        {{ loading ? 'Loading…' : 'Refresh' }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.zone-slider {
  -webkit-appearance: none;
  appearance: none;
  direction: ltr;
  height: 0.375rem;
  border-radius: 9999px;
  background: rgba(161, 161, 170, 0.35);
  outline: none;
}
.zone-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 1rem;
  height: 1rem;
  border-radius: 9999px;
  background: rgb(59 130 246);
  border: 2px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  cursor: pointer;
}
.zone-slider::-moz-range-thumb {
  width: 1rem;
  height: 1rem;
  border-radius: 9999px;
  background: rgb(59 130 246);
  border: 2px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  cursor: pointer;
}
:global(html.dark) .zone-slider {
  background: rgba(255, 255, 255, 0.12);
}
</style>
